import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { SelectOption } from 'src/app/shared/components/sky-select/sky-select.component';
import { FormControl } from '@angular/forms';

export interface User {
  userName: string;
  currentProvider: ProviderWithZone | null;
  inspectorsModelInfo: Array<{ id: number; name: string }>;
  channels: Array<string>;
}

export interface Provider {
  providerName: string;
  providerCode: string;
  providerDescription: string;
  street: string;
  postCode: string;
  city: string;
}

export interface ZonesByCity {
  name: string;
  value: Array<ParkingZone>;
}

export interface ParkingZone {
  bankAccount: string;
  bankAccountDescription: string;
  cityName: string;
  code: string;
  name: string;
  numericCode: number;
  productCode: string;
  regionId: number;
  regionName: string;
  smsCode: string;
  ussdCode: string;
  zoneDescription: string;
  zoneId: number;
}

export interface ProviderWithZone {
  provider: Provider;
  zones: Array<ParkingZone>;
}

export interface UserApiModel {
  userName: string;
  providersWithZones: Array<ProviderWithZone>;
  inspectorsModelInfo: Array<{ id: number; name: string }>;
  channels: Array<string>;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  public readonly EMPTY_ZONE_ID = -1;

  private readonly _user$ = new ReplaySubject<User>(1);
  private readonly _initialFetch$ = new ReplaySubject<boolean>(1);
  private readonly _availableProviders$ = new BehaviorSubject<Array<ProviderWithZone>>([]);
  private readonly _selectedProvider$ = new BehaviorSubject<ProviderWithZone | null>(null);
  private readonly _zonesByCity$ = new ReplaySubject<Array<ZonesByCity>>(1);
  private readonly _currentZones$ = this._availableProviders$.pipe(
    map(providers => providers.map(provider => provider.zones).flat())
  );

  constructor(private readonly _authService: AuthService, private readonly _http: HttpClient) {
    this.init();
  }

  public get user$(): Observable<User> {
    return this._user$.asObservable();
  }

  public get availableProviders$(): Observable<Array<ProviderWithZone>> {
    return this._availableProviders$.asObservable();
  }

  public get availableProvidersOptions$(): Observable<Array<SelectOption<string>>> {
    return this.availableProviders$.pipe(
      map(providers =>
        Array.from(new Set(providers.map(providerObj => providerObj.provider.providerName)))
          .map(providerName => ({
            id: providerName,
            label: providerName,
          }))
          .filter(option => option.id !== 'Provider for brak')
      )
    );
  }

  public get initialFetch$(): Observable<boolean> {
    return this._initialFetch$.asObservable();
  }

  public get selectedProvider$(): Observable<ProviderWithZone> {
    return this._selectedProvider$.pipe(filter(provider => provider !== null)) as Observable<ProviderWithZone>;
  }

  public setSelectedProvider(providerName: string) {
    this._selectedProvider$.next(
      this._availableProviders$.getValue().find(providerObj => providerObj.provider.providerName === providerName) ||
        null
    );
  }

  public get zonesByCity$(): Observable<Array<ZonesByCity>> {
    return this._zonesByCity$.asObservable();
  }

  public get currentZones$(): Observable<Array<ParkingZone>> {
    return this._currentZones$;
  }

  public getProviderFormControl() {
    return new FormControl(this._selectedProvider$.getValue()?.provider.providerName);
  }

  public getUser$(): Observable<User> {
    return this._http.get<UserApiModel>(environment.apiUrl + '/info/user').pipe(
      tap(response => {
        this._availableProviders$.next(response.providersWithZones);
        this._selectedProvider$.next(response.providersWithZones[0]);
      }),
      map(response => ({
        userName: response.userName,
        currentProvider: this._selectedProvider$.getValue(),
        inspectorsModelInfo: response.inspectorsModelInfo,
        channels: response.channels,
      }))
    );
  }

  private init() {
    this._authService.authenticated$.subscribe(state => {
      if (state.isAuth && state.init) {
        this.getUser$().subscribe(user => {
          this._user$.next(user);
          this._initialFetch$.next(true);
        });
      }

      if (!state.isAuth && state.init) {
        this._authService.logout();
      }
    });
  }
}
